import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonModal,
  IonRouterLink,
  IonRow,
} from '@ionic/react';
import { closeOutline, linkOutline, checkmarkOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useIcons } from '../../../hooks/useIcons';
import { iCrate } from '../../../types/ICrate';
import { openExternalLink } from '../../../utils/openExternalLink';
import './CloudCrates.scss';

interface CloudCrateProps {
  editable: boolean;
  crateData: iCrate;
  onUpdateStreamingLinks?: (updatedLinks: Partial<iCrate>) => void;
  externalModalOpen?: boolean;
  onExternalModalClose?: () => void;
}

interface cloundCrateInfoProps {
  streamLink: string;
  icon: string;
  platform: keyof iCrate;
  editableLink: string;
}

const getPlatformLabel = (platform: string) => {
  const name = platform.replace('Stream', '');
  return name === 'Beatsource' ? 'Beatport' : name;
};

const sanitizeLink = (url: string | undefined): string => {
  if (!url) return '';
  return url.replace(/beatsource\.com/i, 'beatport.com');
};

export const CloudCrates: React.FC<CloudCrateProps> = ({
  editable,
  crateData,
  onUpdateStreamingLinks,
  externalModalOpen,
  onExternalModalClose,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { icons } = useIcons();

  // Sync with external modal state
  useEffect(() => {
    if (externalModalOpen !== undefined) {
      setIsModalOpen(externalModalOpen);
    }
  }, [externalModalOpen]);
  const [streamingLinks, setStreamingLinks] = useState<Partial<iCrate>>();
  const [cloudCrates, setCloudCrates] = useState<cloundCrateInfoProps[]>();
  // Reset streaming links when crateData changes
  useEffect(() => {
    setStreamingLinks({
      StreamSpotify: crateData.StreamSpotify || '',
      StreamAppleMusic: crateData.StreamAppleMusic || '',
      StreamSoundcloud: crateData.StreamSoundcloud || '',
      StreamBeatsource: sanitizeLink(crateData.StreamBeatsource),
      StreamDeezer: crateData.StreamDeezer || '',
      StreamTidal: crateData.StreamTidal || '',
    });
    setCloudCrates([
      {
        streamLink: crateData.StreamSpotify || '',
        icon: 'spotifyFull',
        platform: 'StreamSpotify',
        editableLink: crateData.StreamSpotify || '',
      },
      {
        streamLink: crateData.StreamAppleMusic || '',
        icon: 'apple',
        platform: 'StreamAppleMusic',
        editableLink: crateData.StreamAppleMusic || '',
      },
      {
        streamLink: crateData.StreamSoundcloud || '',
        icon: 'soundcloud',
        platform: 'StreamSoundcloud',
        editableLink: crateData.StreamSoundcloud || '',
      },
      {
        streamLink: sanitizeLink(crateData.StreamBeatsource),
        icon: 'beatport',
        platform: 'StreamBeatsource',
        editableLink: sanitizeLink(crateData.StreamBeatsource),
      },
      {
        streamLink: crateData.StreamDeezer || '',
        icon: 'deezer',
        platform: 'StreamDeezer',
        editableLink: crateData.StreamDeezer || '',
      },
      {
        streamLink: crateData.StreamTidal || '',
        icon: 'tidal',
        platform: 'StreamTidal',
        editableLink: crateData.StreamTidal || '',
      },
    ]);
  }, [crateData]);

  /** Handle updating the streaming links */
  const handleInputChange = useCallback(
    (platform: keyof iCrate, value: string) => {
      const sanitizedValue =
        platform === 'StreamBeatsource' ? sanitizeLink(value) : value;
      setStreamingLinks((prev) => {
        const updated = {
          ...prev,
          [platform]: sanitizedValue,
        };
        return updated;
      });
      setCloudCrates(
        (prevCrates) =>
          prevCrates &&
          prevCrates.map((crate) =>
            crate.platform === platform
              ? { ...crate, editableLink: sanitizedValue }
              : crate,
          ),
      );
    },
    [],
  );

  /** Handle saving the updated links */
  const handleSaveLinks = () => {
    onUpdateStreamingLinks &&
      streamingLinks &&
      onUpdateStreamingLinks(streamingLinks);
    setIsModalOpen(false);
  };

  // Check if there are any valid streaming links
  const hasValidLinks =
    cloudCrates &&
    cloudCrates.some(
      (item) => item.streamLink && item.streamLink.trim() !== '',
    );

  return (
    <>
      {hasValidLinks && (
        <div className="cloud-crate-container">
          {/* Display Cloud Crates Icon if Any Links Exist */}
          <div className="cloud-crates-label">
            {icons.cloudCrates && (
              <IonIcon style={{ width: '100px' }} icon={icons.cloudCrates} />
            )}
          </div>

          <div className="cloud-crate-icons">
            {cloudCrates &&
              cloudCrates
                .filter(
                  (item) => item.streamLink && item.streamLink.trim() !== '',
                )
                .map((item) => (
                  <IonRouterLink
                    key={`cloudCrate-${item.platform}`}
                    className={'cloud-crate-item'}
                    onClick={() => openExternalLink(item.streamLink)}
                    style={{ cursor: 'pointer' }}
                  >
                    {icons[item.icon] && (
                      <IonIcon size="large" icon={icons[item.icon]} />
                    )}
                  </IonRouterLink>
                ))}
          </div>
        </div>
      )}

      {/* Edit Cloud Crates Modal */}
      <IonModal
        isOpen={isModalOpen}
        onDidDismiss={() => {
          setIsModalOpen(false);
          if (onExternalModalClose) onExternalModalClose();
        }}
        className="streaming-links-modal"
      >
        <div className="streaming-links-modal-content">
          {/* Header */}
          <div className="modal-header">
            <div className="header-icon">
              {icons.cloudCrates && <IonIcon icon={icons.cloudCrates} />}
            </div>
            <h2>Edit Streaming Links</h2>
            <IonButton
              fill="clear"
              className="close-btn"
              onClick={() => {
                setIsModalOpen(false);
                if (onExternalModalClose) onExternalModalClose();
              }}
            >
              <IonIcon icon={closeOutline} />
            </IonButton>
          </div>

          {/* Auto-linked note */}
          <div className="auto-linked-note">
            <div className="note-icon">
              <IonIcon icon={checkmarkOutline} />
            </div>
            <div className="note-text">
              <strong>Spotify, Apple Music & Tidal</strong> are automatically
              linked when you save to Cloud Crates.
            </div>
          </div>

          {/* Manual links section */}
          <div className="manual-links-section">
            <div className="section-header">
              <IonIcon icon={linkOutline} />
              <span>Manual Links</span>
            </div>
            <p className="section-description">
              Add links for other streaming services. Use{' '}
              <IonRouterLink
                onClick={() => openExternalLink('https://soundiiz.com/')}
              >
                Soundiiz
              </IonRouterLink>{' '}
              to copy playlists between platforms.
            </p>

            <div className="link-inputs">
              {cloudCrates &&
                cloudCrates
                  .filter(
                    (item) =>
                      item.platform !== 'StreamSpotify' &&
                      item.platform !== 'StreamAppleMusic' &&
                      item.platform !== 'StreamTidal',
                  )
                  .map((item) => (
                    <div
                      key={`cloudCrate-${item.platform}`}
                      className="link-input-row"
                    >
                      <div className="platform-icon">
                        <IonIcon icon={icons[item.icon]} />
                      </div>
                      <div className="input-wrapper">
                        <label>{getPlatformLabel(item.platform)}</label>
                        <IonInput
                          value={item.editableLink}
                          placeholder={`Paste ${getPlatformLabel(item.platform)} playlist URL...`}
                          onIonChange={(e) =>
                            handleInputChange(item.platform, e.detail.value!)
                          }
                          className="link-input"
                        />
                      </div>
                    </div>
                  ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="modal-actions">
            <IonButton
              fill="outline"
              color="medium"
              onClick={() => {
                setIsModalOpen(false);
                if (onExternalModalClose) onExternalModalClose();
              }}
            >
              Cancel
            </IonButton>
            <IonButton fill="solid" color="primary" onClick={handleSaveLinks}>
              <IonIcon icon={checkmarkOutline} slot="start" />
              Save Links
            </IonButton>
          </div>
        </div>
      </IonModal>
    </>
  );
};
